import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Camera, Upload, X } from 'lucide-react';
import { useLots } from '../../../../contexts/LotsContext';
import { useAuth } from '../../../../contexts/AuthContext.jsx';
import useIsMobile from '../../../../hooks/useIsMobile';
import NumericKeypad from '../../../../components/NumericKeypad';

const NewLotForm = () => {
  const navigate = useNavigate();
  const { addLot } = useLots();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    supplier: '',
    quantity: '',
    container_deposit: '',
    unitPrice: '',
    unitCost: '',
    notes: ''
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const isMobile = useIsMobile();

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNumericInput = (value) => {
    if (!activeInput) return;
    
    setFormData(prev => ({
      ...prev,
      [activeInput]: prev[activeInput] + value.toString()
    }));
  };

  const handleNumericDelete = () => {
    if (!activeInput) return;
    
    setFormData(prev => ({
      ...prev,
      [activeInput]: prev[activeInput].slice(0, -1)
    }));
  };

  const handleNumericConfirm = () => {
    if (hasNextInput()) {
      handleNext();
    } else {
      setActiveInput(null);
    }
  };

  const handleNext = () => {
    switch (activeInput) {
      case 'quantity':
        setActiveInput('container_deposit');
        break;
      case 'container_deposit':
        setActiveInput('unitPrice');
        break;
      case 'unitPrice':
        setActiveInput('unitCost');
        break;
      case 'unitCost':
        setActiveInput(null);
        break;
    }
  };

  const hasNextInput = () => {
    switch (activeInput) {
      case 'quantity':
      case 'container_deposit':
      case 'unitPrice':
        return true;
      case 'unitCost':
        return false;
      default:
        return false;
    }
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    // Solo permitir números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    
    if (!user) {
      setError('Debes estar logueado para crear un lote');
      setTimeout(() => {
        navigate('/login', { state: { returnTo: '/lots/new' } });
      }, 2000);
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      // Crear el nuevo lote
      const lotData = {
        name: formData.name,
        supplier: formData.supplier || null,
        quantity: formData.quantity ? Number(formData.quantity) : 0,
        unit: 'bultos',
        container_deposit: formData.container_deposit ? parseFloat(formData.container_deposit) : null,
        unitPrice: formData.unitPrice ? parseFloat(formData.unitPrice) : null,
        unitCost: formData.unitCost ? parseFloat(formData.unitCost) : null,
        notes: formData.notes || null,
        image: imagePreview,
        status: isDraft ? 'draft' : 'active'
      };

      await addLot(lotData);
      
      // Mostrar mensaje de éxito
      setShowSuccess(true);
      
      // Volver a la vista de inventario después de 1.5 segundos
      setTimeout(() => {
        navigate('/', { state: { activeScreen: 'inventory' } });
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="flex items-center p-4">
          <button 
            className="mr-4 text-gray-900 dark:text-white"
            onClick={() => navigate('/', { state: { activeScreen: 'inventory' } })}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold flex-1 text-gray-900 dark:text-white">Nuevo Lote</h1>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 mb-4 bg-red-100 dark:bg-red-900/50 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded">
          <p>{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={(e) => handleSubmit(e, false)} className="p-4 space-y-6">
        {/* Image Upload */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Imagen (opcional)
          </label>
          <div className="flex gap-2">
            {/* Camera button */}
            <label className="flex-1">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer">
                <Camera className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Cámara</span>
              </div>
            </label>

            {/* Gallery button */}
            <label className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer">
                <Upload className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Galería</span>
              </div>
            </label>
          </div>

          {/* Image preview */}
          {imagePreview && (
            <div className="relative">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="w-full h-48 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                className="absolute top-2 right-2 p-1 bg-white dark:bg-gray-800 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Nombre <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <input
            type="text"
            maxLength={50}
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            placeholder="Ej: Morrón Rojo"
          />
        </div>

        {/* Supplier */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Proveedor (opcional)
          </label>
          <input
            type="text"
            value={formData.supplier}
            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
            className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            placeholder="Ej: Juan Pérez"
          />
        </div>

        {/* Quantity */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Cantidad (opcional)
          </label>
          <div className="flex items-center">
            <input
              type="text"
              inputMode="decimal"
              value={formData.quantity}
              onChange={(e) => handleInputChange(e, 'quantity')}
              onFocus={() => setActiveInput('quantity')}
              readOnly={isMobile}
              className={`flex-1 p-3 border-2 dark:border-gray-700 rounded-l-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0 read-only:bg-white dark:read-only:bg-gray-800 ${activeInput === 'quantity' ? 'border-blue-500 dark:border-blue-400' : ''}`}
              placeholder="0.00"
            />
            <span className="px-4 py-3 bg-gray-100 dark:bg-gray-700 border dark:border-gray-700 border-l-0 rounded-r-lg text-gray-600 dark:text-gray-400">
              bultos
            </span>
          </div>
        </div>

        {/* Container Deposit */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Seña por envase (opcional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={formData.container_deposit}
              onChange={(e) => handleInputChange(e, 'container_deposit')}
              onFocus={() => setActiveInput('container_deposit')}
              readOnly={isMobile}
              className={`w-full p-3 pl-7 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0 read-only:bg-white dark:read-only:bg-gray-800 ${activeInput === 'container_deposit' ? 'border-blue-500 dark:border-blue-400' : ''}`}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Unit Price */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Precio unitario (opcional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={formData.unitPrice}
              onChange={(e) => handleInputChange(e, 'unitPrice')}
              onFocus={() => setActiveInput('unitPrice')}
              readOnly={isMobile}
              className={`w-full p-3 pl-7 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0 read-only:bg-white dark:read-only:bg-gray-800 ${activeInput === 'unitPrice' ? 'border-blue-500 dark:border-blue-400' : ''}`}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Unit Cost */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Costo unitario (opcional)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={formData.unitCost}
              onChange={(e) => handleInputChange(e, 'unitCost')}
              onFocus={() => setActiveInput('unitCost')}
              readOnly={isMobile}
              className={`w-full p-3 pl-7 border-2 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0 read-only:bg-white dark:read-only:bg-gray-800 ${activeInput === 'unitCost' ? 'border-blue-500 dark:border-blue-400' : ''}`}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Notas (opcional)
          </label>
          <input
            type="text"
            maxLength={50}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            placeholder="Notas adicionales..."
          />
        </div>

        {/* Botones */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={isLoading || !formData.name}
            className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Dejar pendiente
          </button>
          <button
            type="submit"
            disabled={isLoading || !formData.name}
            className="flex-1 py-3 bg-blue-500 dark:bg-blue-600 text-white rounded-lg disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:text-gray-500 dark:disabled:text-gray-400 hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Creando...
              </div>
            ) : (
              'Crear lote'
            )}
          </button>
        </div>
      </form>

      {/* Teclado numérico - Solo visible en dispositivos móviles cuando hay un input activo */}
      {isMobile && activeInput && (
        <div className="fixed inset-x-0 bottom-0 z-50">
          <NumericKeypad
            onInput={handleNumericInput}
            onDelete={handleNumericDelete}
            onConfirm={handleNumericConfirm}
            onNext={handleNext}
            hasNext={hasNextInput()}
          />
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">¡Lote creado con éxito!</h3>
              <p className="text-gray-500 dark:text-gray-400">Redirigiendo al inventario...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewLotForm;